import { useEffect, useState, type ReactNode } from 'react';
import {
  IconClose,
  IconEngagement,
  IconFeatures,
  IconMenu,
  IconMoon,
  IconOverview,
  IconPlatforms,
  IconReleases,
  IconSignOut,
  IconStability,
  IconSun,
} from '../Icons';
import { RANGES, type PlatformFilter, type RangeKey } from '../../data/types';
import { PLATFORM_COLORS, filterLabel, platformsFor } from '../../lib/platform';
import type { SourceKind } from '../../data/client';
/*
 * Imported, not written as a literal path. `src="/public/…"` only resolved in
 * dev, where Vite also serves the project root — in a build, `public/` is copied
 * *to* the site root, so that URL 404s and the logo vanished once deployed.
 * An import is rewritten to the hashed emitted asset and fails the build if the
 * file is missing, instead of failing silently in production.
 */
import logoUrl from '../../assets/pyraxis-logo.png';

export type PageId = 'overview' | 'platforms' | 'engagement' | 'features' | 'stability' | 'releases';

/**
 * The reports, and what each one is for.
 *
 * `blurb` lives here rather than at the top of each page component so that the
 * masthead can render the whole header — title, context and description — as
 * one block. Six pages each opening with their own differently-shaped paragraph
 * is how a dashboard ends up with six different headers.
 */
export const PAGES: {
  id: PageId;
  label: string;
  title: string;
  blurb: string;
  Icon: typeof IconOverview;
}[] = [
  {
    id: 'overview',
    label: 'Overview',
    title: 'Overview',
    blurb: 'Fleet-wide health across all five platforms — who is using Pyraxis, and whether that is growing.',
    Icon: IconOverview,
  },
  {
    id: 'platforms',
    label: 'Platforms',
    title: 'Platform breakdown',
    blurb:
      'Each platform on its own terms. The desktop and mobile clients share a feature set but not a runtime, and the differences below are where release and support effort actually goes.',
    Icon: IconPlatforms,
  },
  {
    id: 'engagement',
    label: 'Engagement',
    title: 'Engagement & retention',
    blurb:
      'How often people come back, and when they browse. Retention is the metric that says whether new installs were worth anything.',
    Icon: IconEngagement,
  },
  {
    id: 'features',
    label: 'Features',
    title: 'Feature adoption',
    blurb:
      'Every shipped feature, and how many people touch it. A feature that never leaves single digits on any platform is a candidate for removal — that is what this page is for.',
    Icon: IconFeatures,
  },
  {
    id: 'stability',
    label: 'Stability',
    title: 'Stability & performance',
    blurb:
      'Crash-free rate, open issues, and the performance budgets every platform is held to. A build below the crash-free floor is the signal to halt its rollout.',
    Icon: IconStability,
  },
  {
    id: 'releases',
    label: 'Releases',
    title: 'Release adoption',
    blurb:
      'How fast each fleet moves onto the newest build. Desktop ships as a packaged app the user has to update; Android and iOS ride the store and converge in days.',
    Icon: IconReleases,
  },
];

interface Props {
  page: PageId;
  onPage: (p: PageId) => void;
  range: RangeKey;
  onRange: (r: RangeKey) => void;
  platform: PlatformFilter;
  onPlatform: (p: PlatformFilter) => void;
  theme: 'dark' | 'light';
  onTheme: () => void;
  /** Omitted on sample data, where there is no session to end. */
  onSignOut?: () => void;
  source: SourceKind;
  /** Newest shipped build per client, from the snapshot. Null while unknown. */
  versions?: { desktop: string | null; mobile: string | null };
  /** A background refresh is in flight. */
  refreshing?: boolean;
  /** epoch ms of the last successful load, for the freshness line. */
  updatedAt?: number | null;
  /** epoch ms the next background poll is due, for the countdown. */
  nextAt?: number | null;
  children: ReactNode;
}

/** How long "Updated" stays up after a refresh before the countdown resumes. */
const UPDATED_FLASH_MS = 2500;

/** Below this the sidebar is a drawer rather than a column. */
const DRAWER_BREAKPOINT = '(max-width: 999px)';

interface Freshness {
  text: string | null;
  /** 0 → just polled, 1 → the next poll is due. Null when not counting down. */
  progress: number | null;
}

/**
 * The live refresh state, as text and as a fraction.
 *
 * A plain "Updated just now" was indistinguishable from a page that had quietly
 * stopped polling — the whole point of a background refresh is that you cannot
 * see it happen, so the only proof it is alive is a number that moves. The
 * fraction drives the rail under the topbar; the text is its accessible
 * equivalent. Ticks once a second, which is the resolution being displayed.
 */
function useRefreshStatus(
  updatedAt: number | null | undefined,
  nextAt: number | null | undefined,
  refreshing: boolean,
): Freshness {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (refreshing) return { text: 'Updating…', progress: 1 };
  if (!updatedAt) return { text: null, progress: null };
  if (now - updatedAt < UPDATED_FLASH_MS) return { text: 'Updated', progress: 0 };
  if (!nextAt) return { text: 'Live updates paused', progress: null };

  // Never render "in 0s": the poll fires on the same tick that would show it,
  // so a floor of 1 keeps the countdown from flickering through zero.
  const seconds = Math.max(1, Math.ceil((nextAt - now) / 1000));
  const span = nextAt - updatedAt;
  const progress = span > 0 ? Math.min(1, Math.max(0, (now - updatedAt) / span)) : null;
  return { text: `Next update in ${seconds}s`, progress };
}

/** True while the viewport is narrow enough for the drawer layout. */
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(DRAWER_BREAKPOINT).matches);
  useEffect(() => {
    const query = window.matchMedia(DRAWER_BREAKPOINT);
    const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

const SOURCE_COPY: Record<SourceKind, { dot: string; text: string }> = {
  mock: { dot: 'var(--status-warning)', text: 'Sample data' },
  live: { dot: 'var(--status-good)', text: 'Live telemetry' },
  fallback: { dot: 'var(--status-critical)', text: 'API unreachable — sample data' },
  // Reachable but unauthenticated. The sign-in screen replaces the whole page,
  // so this only shows in the moment before that renders.
  unauthorized: { dot: 'var(--status-warning)', text: 'Signed out' },
};

/**
 * Eight options is too many for a segmented control in a toolbar that also
 * carries a range picker, so the platform filter is a grouped select. The
 * swatch strip beside it keeps the current selection's series colors visible.
 */
const FILTER_GROUPS: { label: string; options: PlatformFilter[] }[] = [
  { label: 'Everything', options: ['all'] },
  { label: 'By client', options: ['desktop', 'mobile'] },
  { label: 'Desktop platforms', options: ['windows', 'macos', 'linux'] },
  { label: 'Mobile platforms', options: ['android', 'ios'] },
];

export function Shell({
  page,
  onPage,
  range,
  onRange,
  platform,
  onPlatform,
  theme,
  onTheme,
  onSignOut,
  source,
  versions,
  refreshing = false,
  updatedAt = null,
  nextAt = null,
  children,
}: Props) {
  const current = PAGES.find((p) => p.id === page)!;
  const src = SOURCE_COPY[source];
  const active = platformsFor(platform);
  const { text: freshness, progress } = useRefreshStatus(updatedAt, nextAt, refreshing);
  const narrow = useIsNarrow();
  const [menuOpen, setMenuOpen] = useState(false);

  // Escape closes the drawer, and a widened window drops it entirely — coming
  // back from a rotation with an open drawer over a full sidebar is a state
  // nobody asked for.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (!narrow) setMenuOpen(false);
  }, [narrow]);

  // The page behind a drawer must not scroll under the thumb.
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  const rangeTitle = RANGES.find((r) => r.key === range)?.title ?? range;
  const drawer = narrow && menuOpen;

  return (
    <div className={`shell${drawer ? ' shell--menu' : ''}`}>
      {/* Backdrop. A button rather than a div so a tap is a real activation and
          the drawer is dismissible without a pointer. */}
      <button
        type="button"
        className="scrim"
        hidden={!drawer}
        aria-label="Close menu"
        onClick={() => setMenuOpen(false)}
      />

      <aside className="sidebar" inert={narrow && !menuOpen}>
        <div className="brand">
          <img className="brand__logo" src={logoUrl} alt="" />
          <div className="brand__text">
            <div className="brand__name">Pyraxis</div>
            <div className="brand__sub">Analytics</div>
          </div>
          <button
            type="button"
            className="icon-btn sidebar__close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <IconClose size={15} />
          </button>
        </div>

        <nav className="nav" aria-label="Reports">
          <span className="nav__label">Reports</span>
          {PAGES.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              className="nav__item"
              aria-current={page === id ? 'page' : undefined}
              onClick={() => {
                onPage(id);
                setMenuOpen(false);
              }}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </nav>

        <div className="sidebar__foot">
          {/* Shipping versions, read from the snapshot rather than authored, so
              a release moves this line the same day it moves the charts. */}
          {versions && (versions.desktop || versions.mobile) ? (
            <div className="fleet">
              <span className="fleet__label">Shipping</span>
              {versions.desktop ? (
                <div className="fleet__row">
                  <span>Desktop</span>
                  <span className="mono fleet__ver">{versions.desktop}</span>
                </div>
              ) : null}
              {versions.mobile ? (
                <div className="fleet__row">
                  <span>Mobile</span>
                  <span className="mono fleet__ver">{versions.mobile}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="src-badge">
            {/* The one visible sign a refresh is happening: the source dot
                breathes for a moment. Deliberately not a spinner or an overlay —
                a poll every minute must not read as the page reloading. */}
            <span
              className={`dot${refreshing ? ' dot--pulse' : ''}`}
              style={{ background: src.dot }}
            />
            {src.text}
          </div>
          {freshness ? (
            <div className="src-badge" aria-live="polite">
              {freshness}
            </div>
          ) : null}

          {onSignOut ? (
            <button type="button" className="sidebar__signout" onClick={onSignOut}>
              <IconSignOut size={14} />
              Sign out
            </button>
          ) : null}
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="icon-btn topbar__menu"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            <IconMenu size={16} />
          </button>

          <span className="topbar__crumb">
            <span className="topbar__crumb-root">Pyraxis Analytics</span>
            <span className="topbar__crumb-sep" aria-hidden="true">
              /
            </span>
            {current.label}
          </span>

          <div className="topbar__controls">
            <div className="select-wrap">
              <span className="select-swatches" aria-hidden="true">
                {active.map((p) => (
                  <span key={p} className="select-swatch" style={{ background: PLATFORM_COLORS[p] }} />
                ))}
              </span>
              <select
                className="select"
                value={platform}
                onChange={(e) => onPlatform(e.target.value as PlatformFilter)}
                aria-label="Platform filter"
              >
                {FILTER_GROUPS.map((group) =>
                  group.options.length === 1 ? (
                    <option key={group.options[0]} value={group.options[0]}>
                      {filterLabel(group.options[0])}
                    </option>
                  ) : (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {filterLabel(opt)}
                        </option>
                      ))}
                    </optgroup>
                  ),
                )}
              </select>
            </div>

            <div className="segmented" role="group" aria-label="Date range">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className="segmented__btn"
                  aria-pressed={range === r.key}
                  aria-label={r.title}
                  title={r.title}
                  onClick={() => onRange(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Outside the control row on purpose: that row scrolls sideways on a
              phone, and the theme toggle is the one control that must never
              scroll out of reach. */}
          <button
            type="button"
            className="icon-btn topbar__theme"
            onClick={onTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? <IconSun size={15} /> : <IconMoon size={15} />}
          </button>
        </header>

        {/*
         * The refresh rail.
         *
         * A two-pixel line that fills toward the next poll and flashes on
         * arrival. It is the freshness text made continuous: at a glance, a
         * moving rail means the numbers below are being kept current, and a
         * dead one means they are not. Driven by the same `nextAt` deadline the
         * countdown uses, so it cannot drift from what the sidebar claims.
         */}
        <div
          className={`rail${refreshing ? ' rail--live' : ''}`}
          role="presentation"
          title={freshness ?? undefined}
        >
          <span
            className="rail__fill"
            style={{ transform: `scaleX(${progress ?? 0})`, opacity: progress === null ? 0 : 1 }}
          />
        </div>

        {/* Keyed on the report, so switching pages replays the entrance rather
            than swapping the content in place. The only motion in the app body:
            it marks a navigation, which is the one thing here a person actually
            did. */}
        <main className="page" key={page}>
          <div className="masthead">
            <p className="masthead__eyebrow">
              <span className={`dot${refreshing ? ' dot--pulse' : ''}`} style={{ background: src.dot }} />
              {src.text}
              <span className="masthead__dot" aria-hidden="true" />
              {rangeTitle}
              <span className="masthead__dot" aria-hidden="true" />
              {filterLabel(platform)}
            </p>
            <h1 className="masthead__title">{current.title}</h1>
            <p className="masthead__blurb">{current.blurb}</p>
          </div>

          {children}
        </main>
      </div>
    </div>
  );
}
